import type {
  MeResponse,
  ChargeClassStatusRequest,
  ChargeClassStatusResponse,
  ChargeClassDraftsResponse,
  ChargeClassDraftsBulkRequest,
  ChargeClassDraftsBulkResponse,
  InviteClientRequest,
  InviteClientResponse,
  ListClientsResponse,
  UpdateClientRequest,
  ClientMutationResponse,
  GetAccountProfileResponse,
  UpdateAccountProfileRequest,
  UpdateAccountProfileResponse,
  ListServicesResponse,
  ListServicesAdminResponse,
  GetServiceResponse,
  ServiceMutationResponse,
  ServiceActivePreflightResponse,
  ServiceGroupsResponse,
  LinkServiceGroupResponse,
  ProvisionServiceHoldersResponse,
  CreateServiceRequest,
  UpdateServiceRequest,
  ListVendorsResponse,
  CreateVendorRequest,
  UpdateVendorRequest,
  VendorMutationResponse,
  ListRecurringExpensesResponse,
  CreateRecurringExpenseRequest,
  UpdateRecurringExpenseRequest,
  RecurringExpenseMutationResponse,
  DeleteRecurringExpenseResponse,
  ListTimeEntriesResponse,
  OpenTimersResponse,
  StartTimerRequest,
  CreateTimeEntryRequest,
  UpdateTimeEntryRequest,
  TimeEntryMutationResponse,
  DeleteTimeEntryResponse,
  ListMileageResponse,
  CreateMileageRequest,
  UpdateMileageRequest,
  MileageMutationResponse,
  DeleteMileageResponse,
  ListProjectsResponse,
  CreateProjectRequest,
  UpdateProjectRequest,
  ProjectMutationResponse,
  DeleteProjectResponse,
  InvoiceListScope,
  ListInvoicesResponse,
  ResolveInvoiceTaxResponse,
  ArAgingResponse,
  CreateInvoiceRequest,
  UpdateInvoiceRequest,
  InvoiceMutationResponse,
  DeleteInvoiceResponse,
  GenerateProjectInvoiceRequest,
  PullItemRef,
  ComposeMonthlyRequest,
  ComposeMonthlyResponse,
  UnbilledItemsResponse,
  RecordPaymentRequest,
  RecordSettlementRequest,
  SettlementMutationResponse,
  PaymentMutationResponse,
  NotatePaymentRequest,
  NotatePaymentResponse,
  ConfirmPaymentRequest,
  RejectPaymentRequest,
  ListPaymentsResponse,
  PaymentRegisterQuery,
  PaymentRegisterResponse,
  MoneyRegisterQuery,
  MoneyRegisterResponse,
  AllocatableInvoicesResponse,
  EarlyPayQuoteResponse,
  ListAdjustmentsResponse,
  CreateAdjustmentRequest,
  ApplyCreditRequest,
  ApplyCreditsRequest,
  AdjustmentMutationResponse,
  ListMyInvoicesResponse,
  ListDocumentsResponse,
  DocumentMutationResponse,
  UpdateDocumentRequest,
  BulkUpdateDocumentsRequest,
  BulkUpdateDocumentsResponse,
  DeleteDocumentResponse,
  DocumentOwnerType,
  ListClientDocumentsResponse,
  ClientDocumentMutationResponse,
  DeleteClientDocumentResponse,
  ListExpensesResponse,
  CreateExpenseRequest,
  UpdateExpenseRequest,
  ExpenseMutationResponse,
  DeleteExpenseResponse,
  AttachmentMutationResponse,
  EnsureInternalClientResponse,
  ListBusinessesResponse,
  Business,
  AvatarMutationResponse,
  ClientLogoMutationResponse,
  ServiceLogoMutationResponse,
  PackageLogoMutationResponse,
  ListClientUsersResponse,
  AddClientUserRequest,
  AddClientUserResponse,
  RemoveClientUserResponse,
  UserLookupRequest,
  UserLookupResponse,
  ResetMemberPasswordResponse,
  ResetMemberMfaResponse,
  ChangeMemberEmailResponse,
  GetBusinessResponse,
  UpdateBusinessConfigRequest,
  BusinessConfigMutationResponse,
  ListClientServicesResponse,
  AssignClientServiceRequest,
  UpdateClientServiceRequest,
  ClientServiceMutationResponse,
  ListNonPaymentSuspendedResponse,
  ResumeNonPaymentRequest,
  ResumeNonPaymentResponse,
  ListPackagesAdminResponse,
  GetPackageResponse,
  PackageMutationResponse,
  PackageActivePreflightResponse,
  CreatePackageRequest,
  UpdatePackageRequest,
  ListClientPackagesResponse,
  AssignClientPackageRequest,
  UpdateClientPackageRequest,
  ClientPackageMutationResponse,
  PackageChildDisposition,
  GetClientCatalogResponse,
  SubscribeServiceResponse,
  SubscribePackageResponse,
  ListNotificationsResponse,
  MarkNotificationsViewedResponse,
  DismissNotificationResponse,
  MarkNotificationsSeenResponse,
  ListAdminNotificationsResponse,
  CreateNotificationRequest,
  UpdateNotificationRequest,
  NotificationMutationResponse,
  RemoveNotificationResponse,
  WelcomeStatusResponse,
  WelcomeMutationResponse,
  IssueClientServiceCredentialRequest,
  IssueClientServiceCredentialResponse,
  VoidClientServiceCredentialResponse,
  RevealCredentialResponse,
  AttestCredentialResponse,
  CreateRemittanceRequest,
  RemittanceBalancesResponse,
  RemittanceListQuery,
  RemittanceListResponse,
  RemittanceMutationResponse,
  BulkTaxCategoryRequest,
  BulkTaxCategoryResponse,
  TaxReviewResponse,
  TaxReviewStatusResponse,
  CompleteTaxReviewRequest,
  CompleteTaxReviewResponse,
  PendingCreditsResponse,
  ResolvePendingCreditsRequest,
  ResolvePendingCreditsResponse,
  VoidCreditLapseRequest,
  VoidCreditLapseResponse,
} from '@aralere/shared-types';

class ApiError extends Error {
  constructor(public status: number, message: string, public details?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      // ignore
    }
    throw new ApiError(res.status, `API ${path} failed with ${res.status}`, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Upload a raw image body (04 #3). The image's own MIME is the Content-Type and the File is the
 * body verbatim — NOT multipart and NOT JSON — matching the API's raw-body upload handlers, which
 * content-sniff the bytes before writing. JSON response is parsed like `request`.
 */
async function rawRequest<T>(path: string, file: Blob): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      // ignore
    }
    throw new ApiError(res.status, `API ${path} failed with ${res.status}`, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  me: (): Promise<MeResponse> => request<MeResponse>('/api/me'),
  brand: (): Promise<{ brand: Business }> => request<{ brand: Business }>('/api/brand'),
  listServices: (): Promise<ListServicesResponse> =>
    request<ListServicesResponse>('/api/services'),
  // In-app notifications (spec §12 SYSTEM; 08 Q25). The bell/toast read here; the server resolves
  // which notifications the signed-in principal sees by audience AND joins their receipts for the
  // per-user lifecycle. mark-viewed (bell-open, set-once) + dismiss replace the legacy mark-seen.
  notifications: {
    list: (): Promise<ListNotificationsResponse> =>
      request<ListNotificationsResponse>('/api/notifications'),
    /** Set-once "viewed" for every active row (fired when the bell opens) — clears unread + starts
     *  the broadcast 72h clocks. */
    markViewed: (): Promise<MarkNotificationsViewedResponse> =>
      request<MarkNotificationsViewedResponse>('/api/notifications?action=mark-viewed', {
        method: 'POST',
      }),
    /** Close one notification for this user (per-row ×). */
    dismiss: (id: string): Promise<DismissNotificationResponse> =>
      request<DismissNotificationResponse>(
        `/api/notifications?action=dismiss&id=${encodeURIComponent(id)}`,
        { method: 'POST' }
      ),
    /** @deprecated Legacy bell-opened marker — superseded by markViewed (08 Q25). Retired at slice 3. */
    markSeen: (): Promise<MarkNotificationsSeenResponse> =>
      request<MarkNotificationsSeenResponse>('/api/notifications?action=mark-seen', { method: 'POST' }),
  },
  // Welcome modal (spec §6.3). The brand's welcomeMessage content rides on /api/me; these only carry
  // the per-user "already seen this brand's welcome" flag (receipt-backed, cross-device).
  welcome: {
    /** Has the caller already closed the welcome for the current host's brand? */
    status: (): Promise<WelcomeStatusResponse> => request<WelcomeStatusResponse>('/api/welcome'),
    /** Mark it seen (the modal was closed). */
    markSeen: (): Promise<WelcomeMutationResponse> =>
      request<WelcomeMutationResponse>('/api/welcome', { method: 'POST' }),
    /** Reset so it shows again (the Account-settings replay). */
    reset: (): Promise<WelcomeMutationResponse> =>
      request<WelcomeMutationResponse>('/api/welcome', { method: 'DELETE' }),
  },
  // Client self-service Account settings (Build 2). No id parameter by design —
  // the API resolves the caller's own record from the auth principal.
  account: {
    getProfile: (): Promise<GetAccountProfileResponse> =>
      request<GetAccountProfileResponse>('/api/account-profile'),
    updateProfile: (body: UpdateAccountProfileRequest): Promise<UpdateAccountProfileResponse> =>
      request<UpdateAccountProfileResponse>('/api/account-profile', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    // Profile photo + company logo (04 #3) — caller-own-record (no id; resolved from the principal).
    uploadAvatar: (file: File): Promise<AvatarMutationResponse> =>
      rawRequest<AvatarMutationResponse>('/api/account-avatar', file),
    deleteAvatar: (): Promise<AvatarMutationResponse> =>
      request<AvatarMutationResponse>('/api/account-avatar', { method: 'DELETE' }),
    uploadLogo: (file: File): Promise<ClientLogoMutationResponse> =>
      rawRequest<ClientLogoMutationResponse>('/api/account-logo', file),
    deleteLogo: (): Promise<ClientLogoMutationResponse> =>
      request<ClientLogoMutationResponse>('/api/account-logo', { method: 'DELETE' }),
    // Client documents (spec §18; Stage-3 slice 7 inc 2) — caller-own-record. Visibility and
    // capabilities arrive pre-resolved from the server (Q45 default-deny); uploads target one of
    // the caller's ACTIVE projects.
    listMyDocuments: (): Promise<ListClientDocumentsResponse> =>
      request<ListClientDocumentsResponse>('/api/account-documents'),
    // Client billing (spec §7; Stage-4 slice 4) — caller-own-record. Open (issued) + completed
    // (paid) invoices with client-safe lines/payments and released-document deep links.
    listMyBilling: (): Promise<ListMyInvoicesResponse> =>
      request<ListMyInvoicesResponse>('/api/account-invoices'),
    uploadMyDocument: (projectId: string, file: File, opts?: { title?: string; folder?: string }): Promise<ClientDocumentMutationResponse> => {
      const params = new URLSearchParams({ projectId, filename: file.name });
      if (opts?.title) params.set('title', opts.title);
      if (opts?.folder) params.set('folder', opts.folder);
      return rawRequest<ClientDocumentMutationResponse>(`/api/account-documents?${params.toString()}`, file);
    },
    deleteMyDocument: (id: string, ownerId: string): Promise<DeleteClientDocumentResponse> =>
      request<DeleteClientDocumentResponse>(
        `/api/account-documents?id=${encodeURIComponent(id)}&ownerId=${encodeURIComponent(ownerId)}`,
        { method: 'DELETE' },
      ),
    /** Direct URL for opening (inline) or downloading a visible document (client session; use in an <a>). */
    myDocumentFileUrl: (id: string, ownerId: string, download = false): string =>
      `/api/account-document-file?id=${encodeURIComponent(id)}&ownerId=${encodeURIComponent(ownerId)}${download ? '&download=1' : ''}`,
    /** Direct URL for opening (inline) or downloading the caller's OWN invoice PDF (client session; use in an <a>). */
    myInvoiceFileUrl: (id: string, download = false): string =>
      `/api/account-invoice-file?id=${encodeURIComponent(id)}${download ? '&download=1' : ''}`,
    /** Report a payment made by Zelle/check/money order against an open invoice (§7.8). Creates a
     *  pending-confirmation notation and alerts the admin; settles nothing until an admin confirms. */
    notatePayment: (body: NotatePaymentRequest): Promise<NotatePaymentResponse> =>
      request<NotatePaymentResponse>('/api/account-payments?action=notate', { method: 'POST', body: JSON.stringify(body) }),
    // Client-facing service catalog (Stage 2, spec §5.5/§5.6) — caller-own-record (no id; the server
    // resolves the caller's own client). Returns the client's active services + what they can add.
    getCatalog: (): Promise<GetClientCatalogResponse> =>
      request<GetClientCatalogResponse>('/api/account-services'),
    // §5.5: a client-initiated add must acknowledge the recurring monthly charge (acknowledgeRecurring).
    subscribeToService: (serviceId: string, acknowledgeRecurring: boolean): Promise<SubscribeServiceResponse> =>
      request<SubscribeServiceResponse>('/api/account-services?action=subscribe', {
        method: 'POST',
        body: JSON.stringify({ serviceId, acknowledgeRecurring }),
      }),
    // §5.9: the client adds a PACKAGE to their own account. §5.5 consent required (acknowledgeRecurring);
    // the server drives the shared adopt-not-recreate engine (method 'client-initiated').
    subscribeToPackage: (packageId: string, acknowledgeRecurring: boolean): Promise<SubscribePackageResponse> =>
      request<SubscribePackageResponse>('/api/account-services?action=subscribe-package', {
        method: 'POST',
        body: JSON.stringify({ packageId, acknowledgeRecurring }),
      }),
    // Best-effort: record that the client launched a service (activityLog `service-open`). 204; the
    // caller fires this without awaiting and lets the launch proceed regardless.
    logServiceOpen: (serviceId: string): Promise<void> =>
      request<void>(`/api/account-services?action=open&serviceId=${encodeURIComponent(serviceId)}`, {
        method: 'POST',
      }),
    // 08 Q32: the ONE-TIME sign-in-details reveal (primary contact only) + the optional attestation.
    revealServiceCredential: (serviceId: string): Promise<RevealCredentialResponse> =>
      request<RevealCredentialResponse>(
        `/api/account-services?action=reveal-credential&serviceId=${encodeURIComponent(serviceId)}`,
        { method: 'POST' },
      ),
    attestServiceCredential: (serviceId: string): Promise<AttestCredentialResponse> =>
      request<AttestCredentialResponse>(
        `/api/account-services?action=attest-credential&serviceId=${encodeURIComponent(serviceId)}`,
        { method: 'POST' },
      ),
  },
  admin: {
    listClients: (): Promise<ListClientsResponse> =>
      request<ListClientsResponse>('/api/mgmt-clients'),
    // Client editor (Build 1, 04-deferred-register 2026-06-10)
    updateClient: (id: string, body: UpdateClientRequest): Promise<ClientMutationResponse> =>
      request<ClientMutationResponse>(`/api/mgmt-client?id=${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    // Client company logo (04 #3) — admin manages any business client's logo.
    uploadClientLogo: (id: string, file: File): Promise<ClientLogoMutationResponse> =>
      rawRequest<ClientLogoMutationResponse>(`/api/mgmt-client-logo?id=${encodeURIComponent(id)}`, file),
    deleteClientLogo: (id: string): Promise<ClientLogoMutationResponse> =>
      request<ClientLogoMutationResponse>(`/api/mgmt-client-logo?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),
    // Any user's profile photo (04 #13 / 08 Q12) — platform-admin lane. Members also get an Entra
    // photo push server-side (best-effort); guests are Cosmos-only.
    uploadUserPhoto: (userId: string, file: File): Promise<AvatarMutationResponse> =>
      rawRequest<AvatarMutationResponse>(`/api/mgmt-user-photo?userId=${encodeURIComponent(userId)}`, file),
    deleteUserPhoto: (userId: string): Promise<AvatarMutationResponse> =>
      request<AvatarMutationResponse>(`/api/mgmt-user-photo?userId=${encodeURIComponent(userId)}`, {
        method: 'DELETE',
      }),
    invite: (body: InviteClientRequest): Promise<InviteClientResponse> =>
      request<InviteClientResponse>('/api/mgmt-invite', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    // Manage additional users on a client (04 #15). business derived from the client, not the host.
    listClientUsers: (clientId: string): Promise<ListClientUsersResponse> =>
      request<ListClientUsersResponse>(`/api/mgmt-client-user?clientId=${encodeURIComponent(clientId)}`),
    addClientUser: (clientId: string, body: AddClientUserRequest): Promise<AddClientUserResponse> =>
      request<AddClientUserResponse>(`/api/mgmt-client-user?clientId=${encodeURIComponent(clientId)}`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    // Resolve a person against Entra (member → username/UPN, guest → email) before adding (slice 3).
    lookupClientUser: (body: UserLookupRequest): Promise<UserLookupResponse> =>
      request<UserLookupResponse>('/api/mgmt-client-user-lookup', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    removeClientUser: (clientId: string, userId: string): Promise<RemoveClientUserResponse> =>
      request<RemoveClientUserResponse>(
        `/api/mgmt-client-user?clientId=${encodeURIComponent(clientId)}&userId=${encodeURIComponent(userId)}`,
        { method: 'DELETE' },
      ),
    // Member credential management (04 #9) — surfaced in the editor's Users panel. Members only
    // (guests are managed in their home tenant). clientId scopes the branded email + audit trail.
    resetMemberPassword: (clientId: string, userId: string): Promise<ResetMemberPasswordResponse> =>
      request<ResetMemberPasswordResponse>(
        `/api/mgmt-user-reset?clientId=${encodeURIComponent(clientId)}&userId=${encodeURIComponent(userId)}&action=password`,
        { method: 'POST' },
      ),
    resetMemberMfa: (clientId: string, userId: string): Promise<ResetMemberMfaResponse> =>
      request<ResetMemberMfaResponse>(
        `/api/mgmt-user-reset?clientId=${encodeURIComponent(clientId)}&userId=${encodeURIComponent(userId)}&action=mfa`,
        { method: 'POST' },
      ),
    changeMemberEmail: (
      clientId: string,
      userId: string,
      email: string,
      overrideSharedEmail?: boolean,
    ): Promise<ChangeMemberEmailResponse> =>
      request<ChangeMemberEmailResponse>(
        `/api/mgmt-user-reset?clientId=${encodeURIComponent(clientId)}&userId=${encodeURIComponent(userId)}&action=email`,
        { method: 'POST', body: JSON.stringify({ email, overrideSharedEmail }) },
      ),
    // Service catalog admin (Stage 2, spec §5.1)
    listServices: (): Promise<ListServicesAdminResponse> =>
      request<ListServicesAdminResponse>('/api/mgmt-services'),
    getService: (id: string): Promise<GetServiceResponse> =>
      request<GetServiceResponse>(`/api/mgmt-service?id=${encodeURIComponent(id)}`),
    createService: (body: CreateServiceRequest): Promise<ServiceMutationResponse> =>
      request<ServiceMutationResponse>('/api/mgmt-services', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    updateService: (id: string, body: UpdateServiceRequest): Promise<ServiceMutationResponse> =>
      request<ServiceMutationResponse>(`/api/mgmt-service?id=${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    /** 04 #160 WS-B (02i §8 DP-12): the December review's "select many, set a category" - one
     *  configHistory 'service' row + one auditLog service.update row per CHANGED service, exactly the
     *  editor's trail; unchanged rows write nothing; per-row errors never unwind another row. */
    bulkTaxCategory: (body: BulkTaxCategoryRequest): Promise<BulkTaxCategoryResponse> =>
      request<BulkTaxCategoryResponse>('/api/mgmt-services?action=bulk-tax-category', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    // 04 #160 WS-B: the annual tax-category review (02i T4), COMPUTED ON READ for one brand on the
    // brand's own calendar; `?view=status` is the cheap every-brand trigger the dashboard banner reads;
    // `action=complete` is the one write ("Complete review" / "Confirm all unchanged").
    taxReview: (businessId: string): Promise<TaxReviewResponse> =>
      request<TaxReviewResponse>(`/api/mgmt-tax-review?businessId=${encodeURIComponent(businessId)}`),
    taxReviewStatus: (): Promise<TaxReviewStatusResponse> => request<TaxReviewStatusResponse>('/api/mgmt-tax-review?view=status'),
    completeTaxReview: (businessId: string, body: CompleteTaxReviewRequest = {}): Promise<CompleteTaxReviewResponse> =>
      request<CompleteTaxReviewResponse>(`/api/mgmt-tax-review?businessId=${encodeURIComponent(businessId)}&action=complete`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    // Vendors (spec §10.1, Stage-3 slice 1) — global reference data; `active` is the forward-only
    // visibility toggle (hides from NEW invoice pickers; never touches existing records).
    listVendors: (): Promise<ListVendorsResponse> => request<ListVendorsResponse>('/api/mgmt-vendors'),
    createVendor: (body: CreateVendorRequest): Promise<VendorMutationResponse> =>
      request<VendorMutationResponse>('/api/mgmt-vendors', { method: 'POST', body: JSON.stringify(body) }),
    updateVendor: (id: string, body: UpdateVendorRequest): Promise<VendorMutationResponse> =>
      request<VendorMutationResponse>(`/api/mgmt-vendor?id=${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    // 04 #125 WS-B (s193): REMITTANCES, the money-OUT side of a pass-through class (02g #125 AMENDMENT
    // (A)-(G)). ONE balances derivation (`?view=balances`) feeds the /admin/remittances page, the
    // Settings condensed class line and the vendor "Collected on behalf" block; the register pages on the
    // (dateRemitted DESC, createdAt DESC) composite index; create/void are the pair's write paths.
    remittanceBalances: (query: { businessId?: string | null; payeeVendorId?: string | null } = {}): Promise<RemittanceBalancesResponse> => {
      const qs = new URLSearchParams({ view: 'balances' });
      if (query.businessId) qs.set('businessId', query.businessId);
      if (query.payeeVendorId) qs.set('payeeVendorId', query.payeeVendorId);
      return request<RemittanceBalancesResponse>(`/api/mgmt-remittances?${qs.toString()}`);
    },
    listRemittances: (query: RemittanceListQuery = {}): Promise<RemittanceListResponse> => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) if (v !== null && v !== undefined && v !== '') qs.set(k, String(v));
      const q = qs.toString();
      return request<RemittanceListResponse>(`/api/mgmt-remittances${q ? `?${q}` : ''}`);
    },
    recordRemittance: (body: CreateRemittanceRequest): Promise<RemittanceMutationResponse> =>
      request<RemittanceMutationResponse>('/api/mgmt-remittances', { method: 'POST', body: JSON.stringify(body) }),
    voidRemittance: (id: string, businessId: string, reason: string): Promise<RemittanceMutationResponse> =>
      request<RemittanceMutationResponse>(
        `/api/mgmt-remittance?id=${encodeURIComponent(id)}&businessId=${encodeURIComponent(businessId)}&action=void`,
        { method: 'PATCH', body: JSON.stringify({ reason }) },
      ),
    // Recurring self-expenses (spec §10.2; s43 §1.1) — derive-on-read: the definition is the
    // source of truth (amount edits append amountHistory); list rows carry seat utilization for
    // license entries. DELETE is a TRUE delete behind a strong confirm (owner decision, s44).
    listRecurringExpenses: (serviceId?: string): Promise<ListRecurringExpensesResponse> =>
      request<ListRecurringExpensesResponse>(
        serviceId
          ? `/api/mgmt-recurring-expenses?serviceId=${encodeURIComponent(serviceId)}`
          : '/api/mgmt-recurring-expenses',
      ),
    /** Get-or-create a brand's HOUSE CLIENT (slice 2, s43 record §3) — the self-assign target. */
    ensureInternalClient: (businessId: string): Promise<EnsureInternalClientResponse> =>
      request<EnsureInternalClientResponse>('/api/mgmt-internal-client', {
        method: 'POST',
        body: JSON.stringify({ businessId }),
      }),
    createRecurringExpense: (body: CreateRecurringExpenseRequest): Promise<RecurringExpenseMutationResponse> =>
      request<RecurringExpenseMutationResponse>('/api/mgmt-recurring-expenses', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    updateRecurringExpense: (id: string, body: UpdateRecurringExpenseRequest): Promise<RecurringExpenseMutationResponse> =>
      request<RecurringExpenseMutationResponse>(`/api/mgmt-recurring-expense?id=${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    deleteRecurringExpense: (id: string): Promise<DeleteRecurringExpenseResponse> =>
      request<DeleteRecurringExpenseResponse>(`/api/mgmt-recurring-expense?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),
    // Time tracking (spec §10.4/§8.10; Stage-3 slice 3, s43 §5-§7). One-call list payload: the
    // month's entries + the client's active Type-1 retainers with DERIVED drawn/remaining for that
    // month + the resolved defaults. Draws recompute server-side; billed entries are locked (409).
    listTimeEntries: (clientId: string, month?: string, year?: string): Promise<ListTimeEntriesResponse> =>
      request<ListTimeEntriesResponse>(
        // A month is always YYYY-MM and a year YYYY; slice defensively so a date-shaped value can
        // never reach the API's month/year check (s52 — killed a spurious "month must be YYYY-MM").
        `/api/mgmt-time-entries?clientId=${encodeURIComponent(clientId)}${month ? `&month=${encodeURIComponent(month.slice(0, 7))}` : ''}${year ? `&year=${encodeURIComponent(year.slice(0, 4))}` : ''}`,
      ),
    // s49 open timers: the draft entries oldest-first (index 0 = what a quick End closes);
    // cross-partition when clientId is omitted (the dashboard's quick End).
    listOpenTimers: (clientId?: string): Promise<OpenTimersResponse> =>
      request<OpenTimersResponse>(
        `/api/mgmt-time-entries?open=1${clientId ? `&clientId=${encodeURIComponent(clientId)}` : ''}`,
      ),
    // s49: start a timer (a DRAFT entry) — same POST, minimal body.
    startTimer: (body: StartTimerRequest): Promise<TimeEntryMutationResponse> =>
      request<TimeEntryMutationResponse>('/api/mgmt-time-entries', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    createTimeEntry: (body: CreateTimeEntryRequest): Promise<TimeEntryMutationResponse> =>
      request<TimeEntryMutationResponse>('/api/mgmt-time-entries', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    updateTimeEntry: (id: string, clientId: string, body: UpdateTimeEntryRequest): Promise<TimeEntryMutationResponse> =>
      request<TimeEntryMutationResponse>(
        `/api/mgmt-time-entry?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    deleteTimeEntry: (id: string, clientId: string): Promise<DeleteTimeEntryResponse> =>
      request<DeleteTimeEntryResponse>(
        `/api/mgmt-time-entry?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}`,
        { method: 'DELETE' },
      ),
    // Mileage (spec §10.5; Stage-3 slice 4, Q43). Partitioned by brand (businessId): the list is
    // per-brand for a span, optionally filtered to one client; the billed rate resolves client
    // override → brand rate → margin-over-federal → federal, snapshotted server-side. Billed
    // entries are locked (409). businessId is immutable (the partition key).
    listMileage: (businessId: string, month?: string, year?: string, clientId?: string): Promise<ListMileageResponse> =>
      request<ListMileageResponse>(
        `/api/mgmt-mileage?businessId=${encodeURIComponent(businessId)}${month ? `&month=${encodeURIComponent(month.slice(0, 7))}` : ''}${year ? `&year=${encodeURIComponent(year.slice(0, 4))}` : ''}${clientId ? `&clientId=${encodeURIComponent(clientId)}` : ''}`,
      ),
    createMileage: (body: CreateMileageRequest): Promise<MileageMutationResponse> =>
      request<MileageMutationResponse>('/api/mgmt-mileage', { method: 'POST', body: JSON.stringify(body) }),
    updateMileage: (id: string, businessId: string, body: UpdateMileageRequest): Promise<MileageMutationResponse> =>
      request<MileageMutationResponse>(
        `/api/mgmt-mileage-entry?id=${encodeURIComponent(id)}&businessId=${encodeURIComponent(businessId)}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    deleteMileage: (id: string, businessId: string): Promise<DeleteMileageResponse> =>
      request<DeleteMileageResponse>(
        `/api/mgmt-mileage-entry?id=${encodeURIComponent(id)}&businessId=${encodeURIComponent(businessId)}`,
        { method: 'DELETE' },
      ),
    // Projects (spec §10.3; Stage-3 slice 6). Partitioned by CLIENT (clientId immutable after
    // create). Reads return the doc + COMPUTED totals (never persisted). Omit clientId for the
    // all-ACTIVE overview across every client (s58). Delete only succeeds while no time/mileage/
    // expense line references the project (409 project_in_use).
    listProjects: (clientId?: string): Promise<ListProjectsResponse> =>
      request<ListProjectsResponse>(`/api/mgmt-projects${clientId ? `?clientId=${encodeURIComponent(clientId)}` : ''}`),
    createProject: (body: CreateProjectRequest): Promise<ProjectMutationResponse> =>
      request<ProjectMutationResponse>('/api/mgmt-projects', { method: 'POST', body: JSON.stringify(body) }),
    getProject: (id: string, clientId: string): Promise<ProjectMutationResponse> =>
      request<ProjectMutationResponse>(
        `/api/mgmt-project?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}`,
      ),
    updateProject: (id: string, clientId: string, body: UpdateProjectRequest): Promise<ProjectMutationResponse> =>
      request<ProjectMutationResponse>(
        `/api/mgmt-project?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    deleteProject: (id: string, clientId: string): Promise<DeleteProjectResponse> =>
      request<DeleteProjectResponse>(
        `/api/mgmt-project?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}`,
        { method: 'DELETE' },
      ),
    // Document storage (spec §18; Stage-3 slice 7 — s43 §11). Partitioned by ownerId; the four
    // Q45 triggers are DEFAULT-DENY. Upload rides the raw-body #3 transport; the file URL serves
    // the ORIGINAL bytes (admin session). Client-facing document methods land with account-*.
    listDocuments: (scope?: { ownerType: DocumentOwnerType; ownerId: string }): Promise<ListDocumentsResponse> =>
      request<ListDocumentsResponse>(
        scope
          ? `/api/mgmt-documents?ownerType=${encodeURIComponent(scope.ownerType)}&ownerId=${encodeURIComponent(scope.ownerId)}`
          : '/api/mgmt-documents',
      ),
    uploadDocument: (
      scope: { ownerType: DocumentOwnerType; ownerId: string },
      file: File,
      opts?: {
        title?: string;
        docType?: string;
        folder?: string;
        clientViewable?: boolean;
        clientDownloadable?: boolean;
        deliveryOnPayment?: boolean;
        watermarkBeforePayment?: boolean;
        deliveryInvoiceId?: string;
      },
    ): Promise<DocumentMutationResponse> => {
      const params = new URLSearchParams({ ownerType: scope.ownerType, ownerId: scope.ownerId, filename: file.name });
      if (opts?.title) params.set('title', opts.title);
      if (opts?.docType) params.set('docType', opts.docType);
      if (opts?.folder) params.set('folder', opts.folder);
      if (opts?.clientViewable) params.set('clientViewable', '1');
      if (opts?.clientDownloadable) params.set('clientDownloadable', '1');
      if (opts?.deliveryOnPayment) params.set('deliveryOnPayment', '1');
      if (opts?.watermarkBeforePayment) params.set('watermarkBeforePayment', '1');
      if (opts?.deliveryInvoiceId) params.set('deliveryInvoiceId', opts.deliveryInvoiceId);
      return rawRequest<DocumentMutationResponse>(`/api/mgmt-documents?${params.toString()}`, file);
    },
    updateDocument: (id: string, ownerId: string, body: UpdateDocumentRequest): Promise<DocumentMutationResponse> =>
      request<DocumentMutationResponse>(
        `/api/mgmt-document?id=${encodeURIComponent(id)}&ownerId=${encodeURIComponent(ownerId)}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    deleteDocument: (id: string, ownerId: string, confirmSigned?: boolean): Promise<DeleteDocumentResponse> =>
      request<DeleteDocumentResponse>(
        `/api/mgmt-document?id=${encodeURIComponent(id)}&ownerId=${encodeURIComponent(ownerId)}${confirmSigned ? '&confirmSigned=true' : ''}`,
        { method: 'DELETE' },
      ),
    /** Bulk-apply triggers/folder/invoice link. `override` re-sends after the 409
     *  `existing_invoice_links` guard (s72): a link apply that would overwrite a DIFFERENT
     *  existing deliveryInvoiceId on selected docs requires the explicit confirm. */
    bulkUpdateDocuments: (body: BulkUpdateDocumentsRequest, override?: boolean): Promise<BulkUpdateDocumentsResponse> =>
      request<BulkUpdateDocumentsResponse>(`/api/mgmt-documents-bulk${override ? '?override=1' : ''}`, { method: 'PATCH', body: JSON.stringify(body) }),
    /** Direct URL for viewing (inline) or downloading the ORIGINAL (admin session; use in an <a>). */
    documentFileUrl: (id: string, ownerId: string, download = false): string =>
      `/api/mgmt-document-file?id=${encodeURIComponent(id)}&ownerId=${encodeURIComponent(ownerId)}${download ? '&download=1' : ''}`,
    /** Direct URL for opening (inline) or downloading any invoice PDF (admin session; use in an <a>). */
    invoiceFileUrl: (id: string, clientId: string, download = false): string =>
      `/api/mgmt-invoice-file?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}${download ? '&download=1' : ''}`,
    // Invoices (spec §7; Stage-4 slice 2). Partitioned by CLIENT. Create/read/edit a DRAFT; ISSUE
    // stamps the gapless documentNumber + billedToClientId (both immutable, §7.15); PAY is a minimal
    // full settle (the payments record + partials are a follow-on). Omit clientId to list every
    // client's invoices (newest first); pass one for that client's invoices.
    // scope (04 #71) applies only alongside clientId: 'partition' (default) = invoices ISSUED TO the
    // client; 'entity' = the union with everything BILLED to it, so a business also sees its linked
    // people's business-billed invoices. Omitted = the server default, so existing callers are
    // unchanged. The server echoes the applied scope on the response.
    listInvoices: (clientId?: string, scope?: InvoiceListScope): Promise<ListInvoicesResponse> => {
      if (!clientId) return request<ListInvoicesResponse>('/api/mgmt-invoices');
      const q = new URLSearchParams({ clientId });
      if (scope) q.set('scope', scope);
      return request<ListInvoicesResponse>(`/api/mgmt-invoices?${q.toString()}`);
    },
    // 04 #169 (b): the resolver's own answer for a client ON A DATE (the draft editor's preview for the
    // issue date it carries): the invoice-level rate and provenance, the registration gate and the
    // exemption in force that day. Reads only, writes nothing; asOf omitted = today on the brand clock.
    resolveInvoiceTax: (clientId: string, asOf?: string): Promise<ResolveInvoiceTaxResponse> => {
      const q = new URLSearchParams({ action: 'resolve-tax', clientId });
      if (asOf) q.set('asOf', asOf);
      return request<ResolveInvoiceTaxResponse>(`/api/mgmt-invoices?${q.toString()}`);
    },
    // 04 #160 WS-C M2b (02i §8 DP-13): the LAPSE DIALOG. pendingCredits = the composer's entitlement
    // derivation for the client's next uncomposed month (or the 'YYYY-MM' given) WITHOUT composing: every
    // un-absorbed credit with its source line and the source invoice's state, keyed. resolvePendingCredits
    // applies the admin's choice per key (credit-note / lapse / carry) - validated against ONE re-derived
    // state, the credit notes written first, then the lapses; a failure stops the batch and is reported
    // in `failed`, never silent. voidCreditLapse reverses a stored lapse.
    pendingCredits: (clientId: string, period?: string): Promise<PendingCreditsResponse> => {
      const q = new URLSearchParams({ action: 'pending-credits', clientId });
      if (period) q.set('period', period);
      return request<PendingCreditsResponse>(`/api/mgmt-invoices?${q.toString()}`);
    },
    resolvePendingCredits: (body: ResolvePendingCreditsRequest): Promise<ResolvePendingCreditsResponse> =>
      request<ResolvePendingCreditsResponse>('/api/mgmt-invoices?action=resolve-pending-credits', { method: 'POST', body: JSON.stringify(body) }),
    voidCreditLapse: (body: VoidCreditLapseRequest): Promise<VoidCreditLapseResponse> =>
      request<VoidCreditLapseResponse>('/api/mgmt-invoices?action=void-credit-lapse', { method: 'POST', body: JSON.stringify(body) }),
    // AR aging report (spec §7.19; Stage-4 slice 8a). Open receivables aged off the frozen dueDate,
    // grouped by billing entity + aggregate totals. asOf defaults to today (UTC) server-side.
    arAging: (asOf?: string): Promise<ArAgingResponse> =>
      request<ArAgingResponse>(`/api/mgmt-ar-aging${asOf ? `?asOf=${encodeURIComponent(asOf)}` : ''}`),
    createInvoice: (body: CreateInvoiceRequest): Promise<InvoiceMutationResponse> =>
      request<InvoiceMutationResponse>('/api/mgmt-invoices', { method: 'POST', body: JSON.stringify(body) }),
    getInvoice: (id: string, clientId: string): Promise<InvoiceMutationResponse> =>
      request<InvoiceMutationResponse>(
        `/api/mgmt-invoice?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}`,
      ),
    updateInvoice: (id: string, clientId: string, body: UpdateInvoiceRequest): Promise<InvoiceMutationResponse> =>
      request<InvoiceMutationResponse>(
        `/api/mgmt-invoice?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    issueInvoice: (id: string, clientId: string): Promise<InvoiceMutationResponse> =>
      request<InvoiceMutationResponse>(
        `/api/mgmt-invoice?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}&action=issue`,
        { method: 'PATCH', body: JSON.stringify({}) },
      ),
    /** Void an ISSUED invoice (void-not-delete, §7.15; WS2). Reason required. The server block-and-guides
     *  if the invoice has received/pending payments or an applied credit note (reverse those first), and
     *  auto-lifts any non-payment suspension the now-cancelled debt was driving.
     *  04 #109: pass confirmPaidLateFee after the server 409s late_fee_already_settled, i.e. when a
     *  linked late fee is ALREADY settled and so falls outside the cascade. Same trailing-optional
     *  shape as voidAdjustment's alsoVoidLinkedCreditNote just below. */
    voidInvoice: (id: string, clientId: string, reason: string, confirmPaidLateFee?: boolean): Promise<InvoiceMutationResponse> =>
      request<InvoiceMutationResponse>(
        `/api/mgmt-invoice?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}&action=void`,
        { method: 'PATCH', body: JSON.stringify({ reason, ...(confirmPaidLateFee ? { confirmPaidLateFee: true } : {}) }) },
      ),
    /** WS2: the client's currently non-payment-suspended billed units (services + packages), named for
     *  the admin resume picker. With invoiceId, also returns which units that invoice's lines cover. */
    listNonPaymentSuspended: (clientId: string, invoiceId?: string): Promise<ListNonPaymentSuspendedResponse> =>
      request<ListNonPaymentSuspendedResponse>(
        `/api/mgmt-client-services?clientId=${encodeURIComponent(clientId)}&action=nonpayment-suspended${invoiceId ? `&invoiceId=${encodeURIComponent(invoiceId)}` : ''}`,
      ),
    /** WS2: reactivate the chosen non-payment-suspended units (omit both arrays to resume all). Reuses
     *  the reactivation primitive (tag-strip, reprovision, recompute billingStatus, notify client). */
    resumeNonPaymentServices: (clientId: string, body: ResumeNonPaymentRequest): Promise<ResumeNonPaymentResponse> =>
      request<ResumeNonPaymentResponse>(
        `/api/mgmt-client-services?clientId=${encodeURIComponent(clientId)}&action=resume-nonpayment`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    // The §7.11 unified picker (Stage-4, canonical slice 3). listUnbilled feeds the "Add unbilled
    // items" modal (a client's unbilled billable time / mileage / expense lines; optionally one
    // project's). pullUnbilled appends the selection to a DRAFT — sources are marked billed AT PULL
    // (the 409 entry_billed lock is live from that moment); removing a pulled line on a later save
    // releases its source. generateProjectInvoice = §10.3 "Generate Invoice from Project": one call
    // creates the draft AND pulls all the project's current unbilled items.
    // Slice-4 recurring composer: build the client's Monthly draft for a calendar month
    // ('YYYY-MM'; omitted = the current month). pullUnbilled overrides the brand's auto-pull
    // setting per occasion; force re-composes past the month_already_composed guard (409).
    composeMonthlyInvoice: (body: ComposeMonthlyRequest): Promise<ComposeMonthlyResponse> =>
      request<ComposeMonthlyResponse>('/api/mgmt-invoices?action=compose-monthly', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    listUnbilled: (clientId: string, projectId?: string): Promise<UnbilledItemsResponse> =>
      request<UnbilledItemsResponse>(
        `/api/mgmt-unbilled?clientId=${encodeURIComponent(clientId)}${projectId ? `&projectId=${encodeURIComponent(projectId)}` : ''}`,
      ),
    pullUnbilled: (id: string, clientId: string, items: PullItemRef[]): Promise<InvoiceMutationResponse> =>
      request<InvoiceMutationResponse>(
        `/api/mgmt-invoice?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}&action=pull`,
        { method: 'PATCH', body: JSON.stringify({ items }) },
      ),
    generateProjectInvoice: (body: GenerateProjectInvoiceRequest): Promise<InvoiceMutationResponse> =>
      request<InvoiceMutationResponse>('/api/mgmt-invoices?action=from-project', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    // Payments (spec §7.6/§7.8/§7.16; Stage-4 slice 4 — SUPERSEDES the slice-2 action=pay).
    // Same partition as the invoice (clientId). Recording an ISSUED invoice's payment(s) drives
    // amountPaid/balanceDue; the invoice flips paid only at balance 0. A payment is immutable —
    // correction = void (reason required) + re-record; voiding a settling payment reopens the invoice.
    listPayments: (clientId: string, invoiceId?: string): Promise<ListPaymentsResponse> =>
      request<ListPaymentsResponse>(
        `/api/mgmt-payments?clientId=${encodeURIComponent(clientId)}${invoiceId ? `&invoiceId=${encodeURIComponent(invoiceId)}` : ''}`,
      ),
    // #64 multi-invoice allocation: the OPEN invoices a single non-card receipt can be spread across
    // for ONE billing entity (a business, or a self-billed person), oldest-first with each invoice's
    // remaining non-card balance. Feeds the "Record a payment across invoices" admin picker. recordPayment
    // already accepts the extended request (allocations + billedToClientId + leftover disposition).
    // 04 #69 rule 16: `dateReceived` (YYYY-MM-DD, brand-local) is the date each row's early-pay quote
    // is evaluated on; omitted = the brand's local today. The picker passes its own date field.
    allocatableInvoices: (billedToClientId: string, dateReceived?: string): Promise<AllocatableInvoicesResponse> =>
      request<AllocatableInvoicesResponse>(
        `/api/mgmt-payments?action=allocatable&billedToClientId=${encodeURIComponent(billedToClientId)}${dateReceived ? `&dateReceived=${encodeURIComponent(dateReceived)}` : ''}`,
      ),
    /** 04 #69: the SERVER-COMPUTED early-pay quote for ONE invoice against a candidate payment (02 s97:
     *  the SPA restates no figure). `amount` = the cash toward the invoice (after any tip); omitted =
     *  the pay-in-full-with-discount figure. `dateReceived` omitted = the brand's local today. The
     *  response echoes both, so a dialog re-quotes on every edit of either and never trusts a stale one. */
    earlyPayQuote: (invoiceId: string, clientId: string, opts: { dateReceived?: string; amount?: number; credits?: number } = {}): Promise<EarlyPayQuoteResponse> => {
      const qs = new URLSearchParams({ action: 'early-pay-quote', invoiceId, clientId });
      if (opts.dateReceived) qs.set('dateReceived', opts.dateReceived);
      if (opts.amount !== undefined && Number.isFinite(opts.amount)) qs.set('amount', String(opts.amount));
      // 04 #93 (c) + #69 (s211): the client-credit total the dialog will apply beside the cash, so the
      // server judges the discount against remaining − credits, as recordSettlement does.
      if (opts.credits !== undefined && Number.isFinite(opts.credits) && opts.credits > 0) qs.set('credits', String(opts.credits));
      return request<EarlyPayQuoteResponse>(`/api/mgmt-payments?${qs.toString()}`);
    },
    // 04 #70 the PAYMENTS REGISTER: every receipt across every client, findable by its own PMT
    // number without knowing which invoice it touched. All filters optional; omit them all for the
    // full register, page 1. The response reports page / pageSize / totalCount / totalPages; `page`
    // comes back CLAMPED, so compare it with what you asked for if you need to know you were moved.
    paymentRegister: (query: PaymentRegisterQuery = {}): Promise<PaymentRegisterResponse> => {
      const qs = new URLSearchParams({ action: 'register' });
      for (const [k, v] of Object.entries(query)) if (v !== null && v !== undefined && v !== '') qs.set(k, String(v));
      return request<PaymentRegisterResponse>(`/api/mgmt-payments?${qs.toString()}`);
    },
    /** 04 #93 slice (b): the MONEY REGISTER, one merged date-ordered page over payments AND adjustments
     *  (02d #93 ruling 3). `kinds` empty or omitted = every kind; the #70 register is kinds ['payment'].
     *  method / status need payments in scope and adjustmentStatus needs an adjustment kind in scope, or
     *  the server answers 400 filter_out_of_scope rather than ignoring the filter. Same paging contract as
     *  paymentRegister: `page` comes back CLAMPED; `truncated` true means a stream outgrew the server's
     *  prefix ceiling and the page may be missing rows (say so on screen). */
    moneyRegister: (query: MoneyRegisterQuery = {}): Promise<MoneyRegisterResponse> => {
      const qs = new URLSearchParams({ action: 'money' });
      const { kinds, ...rest } = query;
      if (kinds && kinds.length > 0) qs.set('kinds', kinds.join(','));
      for (const [k, v] of Object.entries(rest)) if (v !== null && v !== undefined && v !== '') qs.set(k, String(v));
      return request<MoneyRegisterResponse>(`/api/mgmt-payments?${qs.toString()}`);
    },
    // 04 #70 the receipt dialog reads the payment FRESH on open rather than trusting accumulated
    // page state, so what an admin sees on a financial surface is always server truth. The endpoint
    // already existed; it was simply never exposed on the SDK.
    getPayment: (id: string, clientId: string): Promise<PaymentMutationResponse> =>
      request<PaymentMutationResponse>(
        `/api/mgmt-payment?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}`,
      ),
    recordPayment: (body: RecordPaymentRequest): Promise<PaymentMutationResponse> =>
      request<PaymentMutationResponse>('/api/mgmt-payments', { method: 'POST', body: JSON.stringify(body) }),
    /** 04 #93 slice (c): settle ONE invoice from cash and applied client credit in a single
     *  composed server op (credit legs first, oldest credit first, then the cash leg; every leg
     *  fail-fast validated before any write; cash hard-capped at remaining minus credits, so no
     *  overpayment on this path). amount 0 with credits = a credit-only settlement (payment null).
     *  A 409 would_release_documents on a credit-only FULL settlement retries with confirmRelease. */
    recordSettlement: (body: RecordSettlementRequest): Promise<SettlementMutationResponse> =>
      request<SettlementMutationResponse>('/api/mgmt-payments?action=settle', { method: 'POST', body: JSON.stringify(body) }),
    voidPayment: (id: string, clientId: string, reason: string, returnedPayment?: boolean): Promise<PaymentMutationResponse> =>
      request<PaymentMutationResponse>(
        `/api/mgmt-payment?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}&action=void`,
        { method: 'PATCH', body: JSON.stringify({ reason, returnedPayment }) },
      ),
    /** Confirm a client-reported payment (§7.8): assigns the PMT number, flips it to received, and
     *  recomputes settlement. Optional receipt/date overrides. */
    confirmPayment: (id: string, clientId: string, body: ConfirmPaymentRequest = {}): Promise<PaymentMutationResponse> =>
      request<PaymentMutationResponse>(
        `/api/mgmt-payment?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}&action=confirm`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    /** Mark a client-reported payment NOT received (§7.8): sets it failed and emails the client to
     *  re-confirm or pay by card. Optional reason. */
    rejectPayment: (id: string, clientId: string, body: RejectPaymentRequest = {}): Promise<PaymentMutationResponse> =>
      request<PaymentMutationResponse>(
        `/api/mgmt-payment?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}&action=reject`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    // Credits / refunds / write-offs (spec §7.20; Stage-4 slice 7). Same partition as the invoice
    // (clientId). createAdjustment issues a credit note (line-based, tax reversed against the invoice's
    // stored values) / write-off / client credit / refund; a fully-satisfying credit returns 409
    // would_release_documents unless the body carries confirmRelease. Adjustments are immutable —
    // correction = voidAdjustment (reason required). applyCredit puts an existing client credit onto an
    // open invoice. listAdjustments also returns the client's available credit balance.
    listAdjustments: (clientId?: string, invoiceId?: string): Promise<ListAdjustmentsResponse> => {
      const qs = new URLSearchParams();
      if (clientId) qs.set('clientId', clientId);
      if (invoiceId) qs.set('invoiceId', invoiceId);
      return request<ListAdjustmentsResponse>(`/api/mgmt-credits?${qs.toString()}`);
    },
    createAdjustment: (body: CreateAdjustmentRequest): Promise<AdjustmentMutationResponse> =>
      request<AdjustmentMutationResponse>('/api/mgmt-credits', { method: 'POST', body: JSON.stringify(body) }),
    getAdjustment: (id: string, clientId: string): Promise<AdjustmentMutationResponse> =>
      request<AdjustmentMutationResponse>(
        `/api/mgmt-credit?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}`,
      ),
    voidAdjustment: (id: string, clientId: string, reason: string, alsoVoidLinkedCreditNote?: boolean): Promise<AdjustmentMutationResponse> =>
      request<AdjustmentMutationResponse>(
        `/api/mgmt-credit?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}&action=void`,
        { method: 'PATCH', body: JSON.stringify({ reason, ...(alsoVoidLinkedCreditNote ? { alsoVoidLinkedCreditNote: true } : {}) }) },
      ),
    applyCredit: (id: string, clientId: string, body: ApplyCreditRequest): Promise<AdjustmentMutationResponse> =>
      request<AdjustmentMutationResponse>(
        `/api/mgmt-credit?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}&action=apply-to-invoice`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    /** 04 #73(b): apply ONE client credit across SEVERAL open invoices of its billing entity in one
     *  action (the #64 mirror). Fail-fast server validation of every row before any write; a 409
     *  would_release_documents aggregates every protected document, retried with confirmRelease.
     *  The response carries `invoices` (every touched invoice), like a #64 recordPayment. */
    applyCredits: (id: string, clientId: string, body: ApplyCreditsRequest): Promise<AdjustmentMutationResponse> =>
      request<AdjustmentMutationResponse>(
        `/api/mgmt-credit?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}&action=apply-to-invoices`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    unapplyCredit: (id: string, clientId: string, invoiceId: string): Promise<AdjustmentMutationResponse> =>
      request<AdjustmentMutationResponse>(
        `/api/mgmt-credit?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}&action=unapply-from-invoice`,
        { method: 'PATCH', body: JSON.stringify({ invoiceId }) },
      ),
    deleteInvoice: (id: string, clientId: string): Promise<DeleteInvoiceResponse> =>
      request<DeleteInvoiceResponse>(
        `/api/mgmt-invoice?id=${encodeURIComponent(id)}&clientId=${encodeURIComponent(clientId)}`,
        { method: 'DELETE' },
      ),
    // One-time / general expenses (spec §10.6; Stage-3 slice 5, line-level s56). Partitioned by
    // brand (businessId); attribution is per LINE. A billed line is locked. Receipts (image/PDF)
    // ride the raw-body endpoint below, admin-only.
    listExpenses: (businessId: string): Promise<ListExpensesResponse> =>
      request<ListExpensesResponse>(`/api/mgmt-expenses?businessId=${encodeURIComponent(businessId)}`),
    createExpense: (body: CreateExpenseRequest): Promise<ExpenseMutationResponse> =>
      request<ExpenseMutationResponse>('/api/mgmt-expenses', { method: 'POST', body: JSON.stringify(body) }),
    updateExpense: (id: string, businessId: string, body: UpdateExpenseRequest): Promise<ExpenseMutationResponse> =>
      request<ExpenseMutationResponse>(
        `/api/mgmt-expense?id=${encodeURIComponent(id)}&businessId=${encodeURIComponent(businessId)}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    deleteExpense: (id: string, businessId: string): Promise<DeleteExpenseResponse> =>
      request<DeleteExpenseResponse>(
        `/api/mgmt-expense?id=${encodeURIComponent(id)}&businessId=${encodeURIComponent(businessId)}`,
        { method: 'DELETE' },
      ),
    // Expense attachments (s56.2) — bill-level, many per expense. Upload posts the raw file with its
    // name in the query; add/remove return the expense's current attachments (the UI re-fetches).
    addExpenseAttachment: (id: string, businessId: string, file: File): Promise<AttachmentMutationResponse> =>
      rawRequest<AttachmentMutationResponse>(
        `/api/mgmt-expense-receipt?id=${encodeURIComponent(id)}&businessId=${encodeURIComponent(businessId)}&filename=${encodeURIComponent(file.name)}`,
        file,
      ),
    deleteExpenseAttachment: (id: string, businessId: string, attachmentId: string): Promise<AttachmentMutationResponse> =>
      request<AttachmentMutationResponse>(
        `/api/mgmt-expense-receipt?id=${encodeURIComponent(id)}&businessId=${encodeURIComponent(businessId)}&attachmentId=${encodeURIComponent(attachmentId)}`,
        { method: 'DELETE' },
      ),
    /** Direct URL for viewing/downloading one attachment (admin session required; use in an <a>/<img>). */
    expenseAttachmentUrl: (id: string, businessId: string, attachmentId: string): string =>
      `/api/mgmt-expense-receipt?id=${encodeURIComponent(id)}&businessId=${encodeURIComponent(businessId)}&attachmentId=${encodeURIComponent(attachmentId)}`,
    // Recurring-expense attachments (s56.2) — same model; recurringExpenses is self-partitioned so
    // only the id is needed.
    addRecurringAttachment: (id: string, file: File): Promise<AttachmentMutationResponse> =>
      rawRequest<AttachmentMutationResponse>(
        `/api/mgmt-recurring-receipt?id=${encodeURIComponent(id)}&filename=${encodeURIComponent(file.name)}`,
        file,
      ),
    deleteRecurringAttachment: (id: string, attachmentId: string): Promise<AttachmentMutationResponse> =>
      request<AttachmentMutationResponse>(
        `/api/mgmt-recurring-receipt?id=${encodeURIComponent(id)}&attachmentId=${encodeURIComponent(attachmentId)}`,
        { method: 'DELETE' },
      ),
    recurringAttachmentUrl: (id: string, attachmentId: string): string =>
      `/api/mgmt-recurring-receipt?id=${encodeURIComponent(id)}&attachmentId=${encodeURIComponent(attachmentId)}`,
    // Service-logo FILE upload (04 #10) — the uploaded image takes precedence over logoUrl; the
    // URL is never cleared by an upload, so removing the upload falls back to it.
    uploadServiceLogo: (id: string, file: File): Promise<ServiceLogoMutationResponse> =>
      rawRequest<ServiceLogoMutationResponse>(`/api/mgmt-service-logo?id=${encodeURIComponent(id)}`, file),
    removeServiceLogo: (id: string): Promise<ServiceLogoMutationResponse> =>
      request<ServiceLogoMutationResponse>(`/api/mgmt-service-logo?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),
    // The Active toggle is the s30 item-4 BULK CASCADE (08 Q29): GET = the preflight (affected
    // counts, package-member warning, pendingBatch); PATCH disable runs the suspend cascade;
    // PATCH enable with a pending batch REQUIRES restore ('restore-all' | 'clean-slate').
    getServiceActivePreflight: (id: string): Promise<ServiceActivePreflightResponse> =>
      request<ServiceActivePreflightResponse>(`/api/mgmt-service-active?id=${encodeURIComponent(id)}`),
    setServiceActive: (
      id: string,
      active: boolean,
      restore?: 'restore-all' | 'clean-slate',
    ): Promise<ServiceMutationResponse> =>
      request<ServiceMutationResponse>(`/api/mgmt-service-active?id=${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ active, ...(restore ? { restore } : {}) }),
      }),
    // s30 item 7 (08 Q31): the Services-editor Entra-groups panel — per-brand bindings + the
    // link-existing-group action + the one-time manual->provisioned promotion run.
    getServiceGroups: (id: string): Promise<ServiceGroupsResponse> =>
      request<ServiceGroupsResponse>(`/api/mgmt-service-groups?id=${encodeURIComponent(id)}`),
    linkServiceGroup: (
      id: string,
      businessId: string,
      entraGroupId: string,
    ): Promise<LinkServiceGroupResponse> =>
      request<LinkServiceGroupResponse>(`/api/mgmt-service-groups?id=${encodeURIComponent(id)}`, {
        method: 'POST',
        body: JSON.stringify({ action: 'link', businessId, entraGroupId }),
      }),
    provisionServiceHolders: (id: string): Promise<ProvisionServiceHoldersResponse> =>
      request<ProvisionServiceHoldersResponse>(`/api/mgmt-service-groups?id=${encodeURIComponent(id)}`, {
        method: 'POST',
        body: JSON.stringify({ action: 'provision-holders' }),
      }),
    listBusinesses: (): Promise<ListBusinessesResponse> =>
      request<ListBusinessesResponse>('/api/mgmt-businesses'),
    // Admin Settings — one business + its §1.4 price-override preview count (Stage 2, spec §5.8/§6).
    getBusiness: (id: string): Promise<GetBusinessResponse> =>
      request<GetBusinessResponse>(`/api/mgmt-business?id=${encodeURIComponent(id)}`),
    /** 04 #60 WS2b: deactivate now (today's brand-local date), schedule a deactivation (a future
     *  date) or reactivate (null) ONE charge class as an IMMEDIATE action outside the staged save. */
    setChargeClassStatus: (id: string, body: ChargeClassStatusRequest): Promise<ChargeClassStatusResponse> =>
      request<ChargeClassStatusResponse>(`/api/mgmt-business?id=${encodeURIComponent(id)}&action=charge-class-status`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    /** 04 #60 WS2b: every DRAFT of the brand that references a charge class (the per-class count
     *  Settings states before a deactivation and on a deactivated class's card). */
    listChargeClassDrafts: (businessId: string): Promise<ChargeClassDraftsResponse> =>
      request<ChargeClassDraftsResponse>(`/api/mgmt-invoices?action=charge-class-drafts&businessId=${encodeURIComponent(businessId)}`),
    /** 04 #60 WS2b: apply one choice (remove / replace / charge-anyway) to every draft that references
     *  an effectively-inactive class; failures are reported per draft. */
    bulkChargeClassDrafts: (body: ChargeClassDraftsBulkRequest): Promise<ChargeClassDraftsBulkResponse> =>
      request<ChargeClassDraftsBulkResponse>('/api/mgmt-invoices?action=charge-class-drafts', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    // Margin / welcome / banner / glass config. `applyMarginToAll` is the §1.4 opt-in (clears
    // per-client price overrides → they re-inherit). Off by default (overrides preserved).
    updateBusinessConfig: (
      id: string,
      body: UpdateBusinessConfigRequest,
    ): Promise<BusinessConfigMutationResponse> =>
      request<BusinessConfigMutationResponse>(`/api/mgmt-business?id=${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    // Per-client service assignment (Stage 2, spec §5.2). Assignment is per-client; admin-assigned
    // (no consent prompt). No billing/provisioning here — those are Stage 4 / #11.
    listClientServices: (clientId: string): Promise<ListClientServicesResponse> =>
      request<ListClientServicesResponse>(`/api/mgmt-client-services?clientId=${encodeURIComponent(clientId)}`),
    assignClientService: (
      clientId: string,
      body: AssignClientServiceRequest,
    ): Promise<ClientServiceMutationResponse> =>
      request<ClientServiceMutationResponse>(`/api/mgmt-client-services?clientId=${encodeURIComponent(clientId)}`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    updateClientService: (
      clientId: string,
      id: string,
      body: UpdateClientServiceRequest,
    ): Promise<ClientServiceMutationResponse> =>
      request<ClientServiceMutationResponse>(
        `/api/mgmt-client-service?clientId=${encodeURIComponent(clientId)}&id=${encodeURIComponent(id)}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    // Soft-cancel (status=cancelled; row preserved for Stage-4 history).
    cancelClientService: (clientId: string, id: string): Promise<ClientServiceMutationResponse> =>
      request<ClientServiceMutationResponse>(
        `/api/mgmt-client-service?clientId=${encodeURIComponent(clientId)}&id=${encodeURIComponent(id)}`,
        { method: 'DELETE' },
      ),
    // One-time credential handoff (08 Q32, s39). The password travels in this request ONCE, straight
    // to the handoff Key Vault; it can never be read back — re-issue instead.
    issueClientServiceCredential: (
      clientId: string,
      id: string,
      body: IssueClientServiceCredentialRequest,
    ): Promise<IssueClientServiceCredentialResponse> =>
      request<IssueClientServiceCredentialResponse>(
        `/api/mgmt-client-service?clientId=${encodeURIComponent(clientId)}&id=${encodeURIComponent(id)}&action=issue-credential`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    voidClientServiceCredential: (clientId: string, id: string): Promise<VoidClientServiceCredentialResponse> =>
      request<VoidClientServiceCredentialResponse>(
        `/api/mgmt-client-service?clientId=${encodeURIComponent(clientId)}&id=${encodeURIComponent(id)}&action=void-credential`,
        { method: 'POST' },
      ),
    // Package catalog admin (Stage 2, spec §5.9). Bundles N services under one price; soft-delete only.
    listPackages: (): Promise<ListPackagesAdminResponse> =>
      request<ListPackagesAdminResponse>('/api/mgmt-packages'),
    getPackage: (id: string): Promise<GetPackageResponse> =>
      request<GetPackageResponse>(`/api/mgmt-package?id=${encodeURIComponent(id)}`),
    createPackage: (body: CreatePackageRequest): Promise<PackageMutationResponse> =>
      request<PackageMutationResponse>('/api/mgmt-packages', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    updatePackage: (id: string, body: UpdatePackageRequest): Promise<PackageMutationResponse> =>
      request<PackageMutationResponse>(`/api/mgmt-package?id=${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    // Package-logo FILE upload (04 #10 parity) — the uploaded image takes precedence over logoUrl;
    // the URL is never cleared by an upload, so removing the upload falls back to it.
    uploadPackageLogo: (id: string, file: File): Promise<PackageLogoMutationResponse> =>
      rawRequest<PackageLogoMutationResponse>(`/api/mgmt-package-logo?id=${encodeURIComponent(id)}`, file),
    removePackageLogo: (id: string): Promise<PackageLogoMutationResponse> =>
      request<PackageLogoMutationResponse>(`/api/mgmt-package-logo?id=${encodeURIComponent(id)}`, {
        method: 'DELETE',
      }),
    // Same s30 item-4 bulk-cascade pair for packages (over the clientPackage + its children).
    getPackageActivePreflight: (id: string): Promise<PackageActivePreflightResponse> =>
      request<PackageActivePreflightResponse>(`/api/mgmt-package-active?id=${encodeURIComponent(id)}`),
    setPackageActive: (
      id: string,
      active: boolean,
      restore?: 'restore-all' | 'clean-slate',
    ): Promise<PackageMutationResponse> =>
      request<PackageMutationResponse>(`/api/mgmt-package-active?id=${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ active, ...(restore ? { restore } : {}) }),
      }),
    // Per-client package assignment (Stage 2, spec §5.9). Adopt-not-recreate; admin-assigned.
    listClientPackages: (clientId: string): Promise<ListClientPackagesResponse> =>
      request<ListClientPackagesResponse>(`/api/mgmt-client-packages?clientId=${encodeURIComponent(clientId)}`),
    assignClientPackage: (
      clientId: string,
      body: AssignClientPackageRequest,
    ): Promise<ClientPackageMutationResponse> =>
      request<ClientPackageMutationResponse>(`/api/mgmt-client-packages?clientId=${encodeURIComponent(clientId)}`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    // Edit override/seats, flip active⇄suspended, OR switch packages (pass switchToPackageId).
    updateClientPackage: (
      clientId: string,
      id: string,
      body: UpdateClientPackageRequest,
    ): Promise<ClientPackageMutationResponse> =>
      request<ClientPackageMutationResponse>(
        `/api/mgmt-client-package?clientId=${encodeURIComponent(clientId)}&id=${encodeURIComponent(id)}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    // Remove with per-service disposition (default: adopted→keep-standalone, created→cancel). Soft-cancel.
    removeClientPackage: (
      clientId: string,
      id: string,
      dispositions?: Record<string, PackageChildDisposition>,
    ): Promise<ClientPackageMutationResponse> =>
      request<ClientPackageMutationResponse>(
        `/api/mgmt-client-package?clientId=${encodeURIComponent(clientId)}&id=${encodeURIComponent(id)}`,
        { method: 'DELETE', body: JSON.stringify({ dispositions }) },
      ),
    // §12 notification authoring (admin composer; broadcasts only). Trail = auditLog notification.*.
    notifications: {
      list: (): Promise<ListAdminNotificationsResponse> =>
        request<ListAdminNotificationsResponse>('/api/mgmt-notifications'),
      create: (body: CreateNotificationRequest): Promise<NotificationMutationResponse> =>
        request<NotificationMutationResponse>('/api/mgmt-notifications', {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      update: (id: string, body: UpdateNotificationRequest): Promise<NotificationMutationResponse> =>
        request<NotificationMutationResponse>(`/api/mgmt-notifications?id=${encodeURIComponent(id)}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      remove: (id: string): Promise<RemoveNotificationResponse> =>
        request<RemoveNotificationResponse>(`/api/mgmt-notifications?id=${encodeURIComponent(id)}`, {
          method: 'DELETE',
        }),
    },
  },
};

/**
 * Extract the server's human-readable error message from a failed request. The API returns
 * { error: { code, message } }; this digs that out of an ApiError's `details` so the UI can show the
 * real reason (e.g. "the margin calculator is off — enter a price") instead of "API … failed with 400".
 */
export function apiErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const d = err.details as { error?: { message?: string } } | null | undefined;
    if (d && d.error && typeof d.error.message === 'string') return d.error.message;
  }
  return err instanceof Error ? err.message : String(err);
}

export { ApiError };
